export interface Point {
  x: number;
  y: number;
}
export function alignment(points: Point[], width: number, height: number) {
  if (points.length < 468) throw new Error('Missing face landmarks');
  const p = points.slice(0, 468).map((v) => ({ x: v.x * width, y: v.y * height }));
  if (!p.every((v) => Number.isFinite(v.x) && Number.isFinite(v.y)))
    throw new Error('Invalid landmarks');
  const left = { x: (p[33]!.x + p[133]!.x) / 2, y: (p[33]!.y + p[133]!.y) / 2 };
  const right = { x: (p[362]!.x + p[263]!.x) / 2, y: (p[362]!.y + p[263]!.y) / 2 };
  const distance = Math.hypot(right.x - left.x, right.y - left.y);
  if (distance < 10) throw new Error('Face too small');
  const ux = (right.x - left.x) / distance,
    uy = (right.y - left.y) / distance;
  const xs = p.map((v) => v.x * ux + v.y * uy),
    ys = p.map((v) => -v.x * uy + v.y * ux);
  const side =
    Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) * 1.65;
  const cx = (Math.max(...xs) + Math.min(...xs)) / 2,
    cy = (Math.max(...ys) + Math.min(...ys)) / 2 - side * 0.035;
  const s = 512 / side;
  return new DOMMatrix([ux * s, -uy * s, uy * s, ux * s, 256 - cx * s, 256 - cy * s]);
}
export function canvas(width: number, height: number) {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  return c;
}
export async function preparePhoto(file: File) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
  const source = canvas(Math.round(bitmap.width * scale), Math.round(bitmap.height * scale));
  source.getContext('2d')!.drawImage(bitmap, 0, 0, source.width, source.height);
  bitmap.close();
  const base = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1';
  const mod = await import(/* @vite-ignore */ `${base}/vision_bundle.mjs`);
  const vision = await mod.FilesetResolver.forVisionTasks(`${base}/wasm`);
  const detector = await mod.FaceLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath:
        'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
      delegate: 'CPU',
    },
    runningMode: 'IMAGE',
    numFaces: 2,
  });
  let points: Point[];
  try {
    const result = detector.detect(source);
    if (result.faceLandmarks.length !== 1) throw new Error('Use exactly one clearly visible face');
    points = result.faceLandmarks[0];
  } finally {
    detector.close();
  }
  const matrix = alignment(points!, source.width, source.height);
  const crop = canvas(512, 512),
    ctx = crop.getContext('2d')!;
  ctx.setTransform(matrix);
  ctx.drawImage(source, 0, 0);
  const small = canvas(256, 256);
  small.getContext('2d')!.drawImage(crop, 0, 0, 256, 256);
  const rgba = small.getContext('2d')!.getImageData(0, 0, 256, 256).data;
  const image = new Float32Array(3 * 256 * 256);
  for (let i = 0; i < 256 * 256; i++)
    for (let c = 0; c < 3; c++) image[c * 256 * 256 + i] = rgba[i * 4 + c]! / 255;
  return { source, matrix, image };
}
export function composite(source: HTMLCanvasElement, matrix: DOMMatrix, pixels: Float32Array) {
  if (pixels.length !== 3 * 512 * 512) throw new Error('Unexpected renderer output');
  const face = canvas(512, 512),
    ctx = face.getContext('2d')!,
    data = ctx.createImageData(512, 512);
  for (let i = 0; i < 512 * 512; i++) {
    for (let c = 0; c < 3; c++)
      data.data[i * 4 + c] = Math.max(0, Math.min(255, pixels[c * 512 * 512 + i]! * 255));
    data.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(data, 0, 0);
  const mask = canvas(512, 512),
    mc = mask.getContext('2d')!;
  mc.filter = 'blur(15px)';
  mc.fillStyle = 'white';
  mc.beginPath();
  mc.ellipse(256, 265, 153, 187, 0, 0, Math.PI * 2);
  mc.fill();
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(mask, 0, 0);
  const result = canvas(source.width, source.height),
    rc = result.getContext('2d')!;
  rc.drawImage(source, 0, 0);
  rc.setTransform(matrix.inverse());
  rc.drawImage(face, 0, 0);
  return result.toDataURL('image/png');
}
